import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { Table } from "components/ui/Table/Table";
import {
  type AdminContractResponse,
  type AdminTenant,
  type CreateContractInput,
  useAdminContractsApi,
  useAdminSubscriptionPlansApi,
  useAdminTenantsApi,
  useCancelAdminContractApi,
  useCreateAdminContractApi,
} from "hooks/useAdminApi";
import { type FC, useState } from "react";
import { Link } from "react-router-dom";
import { toastPromise } from "utils/toast-utils";

/**
 * 契約管理ページ（SYSTEM_ADMIN専用）
 * テナントを選択して契約の一覧表示・新規作成・プラン変更・キャンセルを行う
 * @return {JSX.Element} 契約管理ページ
 */
export const ContractsPage: FC = () => {
  const { data: tenantsData, loading: tenantsLoading } = useAdminTenantsApi();
  const { data: plansData } = useAdminSubscriptionPlansApi();
  const { createContract, loading: isCreating } = useCreateAdminContractApi();
  const { cancelContract, loading: isCancelling } = useCancelAdminContractApi();

  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showChangePlan, setShowChangePlan] =
    useState<AdminContractResponse | null>(null);
  const [formError, setFormError] = useState("");
  const [cancelConfirm, setCancelConfirm] = useState<string | null>(null);
  const [contractForm, setContractForm] = useState<CreateContractInput>({
    subscriptionPlanId: "",
    billingCycle: "monthly",
    startDate: new Date().toISOString().slice(0, 10),
    endDate: "",
  });
  const [changePlanId, setChangePlanId] = useState("");

  const {
    data: contractsData,
    loading: contractsLoading,
    refetch: refetchContracts,
  } = useAdminContractsApi({
    tenantId: selectedTenantId,
    skip: !selectedTenantId,
  });

  const selectedTenant = tenantsData?.items.find(
    (t: AdminTenant) => t.id === selectedTenantId,
  );

  const handleOpenCreate = () => {
    setFormError("");
    setContractForm({
      subscriptionPlanId: plansData?.items[0]?.id || "",
      billingCycle: "monthly",
      startDate: new Date().toISOString().slice(0, 10),
      endDate: "",
    });
    setShowCreateForm(true);
  };

  const handleCreate = async () => {
    if (
      !selectedTenantId ||
      !contractForm.subscriptionPlanId ||
      !contractForm.startDate ||
      !contractForm.endDate
    ) {
      setFormError("全ての項目を入力してください");
      return;
    }
    setFormError("");
    try {
      await toastPromise(createContract(selectedTenantId, contractForm), {
        success: "契約を作成しました",
        error: "契約の作成に失敗しました",
      });
      setShowCreateForm(false);
      await refetchContracts();
    } catch (_e) {
      // toastPromise がエラー表示済み
    }
  };

  const handleOpenChangePlan = (contract: AdminContractResponse) => {
    setShowChangePlan(contract);
    setChangePlanId("");
    setFormError("");
  };

  const handleChangePlan = async () => {
    if (!showChangePlan || !changePlanId) {
      setFormError("変更先のプランを選択してください");
      return;
    }
    setFormError("");
    try {
      await toastPromise(
        createContract(selectedTenantId, {
          subscriptionPlanId: changePlanId,
          billingCycle: showChangePlan.billingCycle,
          startDate: showChangePlan.startDate,
          endDate: showChangePlan.endDate,
        }),
        {
          success:
            "プランを変更しました（旧契約は自動的に superseded になります）",
          error: "プラン変更に失敗しました",
        },
      );
      setShowChangePlan(null);
      await refetchContracts();
    } catch (_e) {
      // toastPromise がエラー表示済み
    }
  };

  const handleCancelConfirmed = async () => {
    if (!cancelConfirm) return;
    try {
      await toastPromise(cancelContract(selectedTenantId, cancelConfirm), {
        success: "契約をキャンセルしました",
        error: "契約のキャンセルに失敗しました",
      });
      await refetchContracts();
    } catch (_e) {
      // toastPromise がエラー表示済み
    } finally {
      setCancelConfirm(null);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>契約管理</PageTitle>
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin">← 管理画面に戻る</Link>
        </Button>
      </div>

      {/* テナント選択 */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <h2 className="card-title text-color-title">テナント選択</h2>
          {tenantsLoading ? (
            <div className="flex justify-center py-4">
              <Loading size="md" />
            </div>
          ) : (
            <select
              className="select select-bordered select-sm w-full max-w-md"
              value={selectedTenantId}
              onChange={e => setSelectedTenantId(e.target.value)}
            >
              <option value="">テナントを選択してください</option>
              {tenantsData?.items.map((t: AdminTenant) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* 契約一覧 */}
      {selectedTenantId && (
        <div className="card bg-base-100 shadow-md mb-6">
          <div className="card-body">
            <div className="flex justify-between items-center mb-2">
              <h2 className="card-title text-color-title">
                {selectedTenant?.name} の契約
              </h2>
              <Button type="button" size="sm" onClick={handleOpenCreate}>
                新規契約作成
              </Button>
            </div>

            {contractsLoading ? (
              <div className="flex justify-center py-4">
                <Loading size="md" />
              </div>
            ) : contractsData && contractsData.items.length > 0 ? (
              <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                <Table.Root density="compact" zebra stickyHeader>
                  <Table.Head sticky>
                    <tr>
                      <Table.HeaderCell>プラン</Table.HeaderCell>
                      <Table.HeaderCell>サイクル</Table.HeaderCell>
                      <Table.HeaderCell>ステータス</Table.HeaderCell>
                      <Table.HeaderCell>開始日</Table.HeaderCell>
                      <Table.HeaderCell>終了日</Table.HeaderCell>
                      <Table.HeaderCell>作成日</Table.HeaderCell>
                      <Table.HeaderCell> </Table.HeaderCell>
                    </tr>
                  </Table.Head>
                  <Table.Body>
                    {contractsData.items.map(contract => (
                      <Table.Row key={contract.id}>
                        <Table.Cell>{contract.planName}</Table.Cell>
                        <Table.Cell>
                          {contract.billingCycle === "monthly"
                            ? "月次"
                            : "年次"}
                        </Table.Cell>
                        <Table.Cell>
                          <span
                            className={`badge badge-sm ${
                              contract.status === "active"
                                ? "badge-success"
                                : contract.status === "trial"
                                  ? "badge-info"
                                  : contract.status === "cancelled"
                                    ? "badge-error"
                                    : "badge-ghost"
                            }`}
                          >
                            {contract.status}
                          </span>
                        </Table.Cell>
                        <Table.Cell>{contract.startDate}</Table.Cell>
                        <Table.Cell>{contract.endDate}</Table.Cell>
                        <Table.Cell>
                          {new Date(contract.createdAt).toLocaleDateString(
                            "ja-JP",
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {(contract.status === "active" ||
                            contract.status === "trial") && (
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="warning"
                                size="xs"
                                disabled={isCreating}
                                onClick={() => handleOpenChangePlan(contract)}
                              >
                                プラン変更
                              </Button>
                              <Button
                                type="button"
                                variant="destructive"
                                size="xs"
                                disabled={isCancelling}
                                onClick={() => setCancelConfirm(contract.id)}
                              >
                                キャンセル
                              </Button>
                            </div>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">契約がありません</p>
            )}
          </div>
        </div>
      )}

      {/* 契約作成モーダル */}
      {showCreateForm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-color-title">
              新規契約作成 - {selectedTenant?.name}
            </h3>
            {formError && (
              <div className="alert alert-error mt-2">
                <span>{formError}</span>
              </div>
            )}
            <div className="space-y-4 py-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">プラン</span>
                </label>
                <select
                  className="select select-bordered select-sm w-full"
                  value={contractForm.subscriptionPlanId}
                  onChange={e =>
                    setContractForm({
                      ...contractForm,
                      subscriptionPlanId: e.target.value,
                    })
                  }
                >
                  <option value="" disabled>
                    プランを選択
                  </option>
                  {plansData?.items.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">請求サイクル</span>
                </label>
                <select
                  className="select select-bordered select-sm w-full"
                  value={contractForm.billingCycle}
                  onChange={e =>
                    setContractForm({
                      ...contractForm,
                      billingCycle: e.target.value,
                    })
                  }
                >
                  <option value="monthly">月次</option>
                  <option value="annual">年次</option>
                </select>
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">開始日</span>
                </label>
                <input
                  type="date"
                  className="input input-bordered input-sm w-full"
                  value={contractForm.startDate}
                  onChange={e =>
                    setContractForm({
                      ...contractForm,
                      startDate: e.target.value,
                    })
                  }
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">終了日</span>
                </label>
                <input
                  type="date"
                  className="input input-bordered input-sm w-full"
                  value={contractForm.endDate}
                  onChange={e =>
                    setContractForm({
                      ...contractForm,
                      endDate: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowCreateForm(false)}
                disabled={isCreating}
              >
                閉じる
              </Button>
              <Button
                type="button"
                onClick={handleCreate}
                disabled={isCreating}
              >
                {isCreating ? <Loading size="sm" /> : "作成"}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isCreating && setShowCreateForm(false)}
          ></div>
        </div>
      )}

      {/* プラン変更モーダル */}
      {showChangePlan && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-color-title">
              プラン変更 - {selectedTenant?.name}
            </h3>
            {formError && (
              <div className="alert alert-error mt-2">
                <span>{formError}</span>
              </div>
            )}
            <div className="space-y-4 py-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">現在のプラン</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered input-sm w-full"
                  value={showChangePlan.planName}
                  disabled
                />
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">変更先プラン</span>
                </label>
                <select
                  className="select select-bordered select-sm w-full"
                  value={changePlanId}
                  onChange={e => setChangePlanId(e.target.value)}
                >
                  <option value="" disabled>
                    プランを選択
                  </option>
                  {plansData?.items.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-gray-500 bg-base-200 rounded-lg p-3">
                <p>
                  サイクル:{" "}
                  {showChangePlan.billingCycle === "monthly" ? "月次" : "年次"}
                </p>
                <p>
                  期間: {showChangePlan.startDate} 〜 {showChangePlan.endDate}
                </p>
                <p className="mt-2 text-warning">
                  現在の契約は自動的に superseded（上書き）されます。
                </p>
              </div>
            </div>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowChangePlan(null)}
                disabled={isCreating}
              >
                閉じる
              </Button>
              <Button
                type="button"
                variant="warning"
                onClick={handleChangePlan}
                disabled={isCreating}
              >
                {isCreating ? <Loading size="sm" /> : "プランを変更"}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isCreating && setShowChangePlan(null)}
          ></div>
        </div>
      )}

      {/* キャンセル確認モーダル */}
      {cancelConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-color-title">
              契約をキャンセルしますか？
            </h3>
            <p className="py-4 text-gray-600">
              この操作により契約が無効化されます。
            </p>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCancelConfirm(null)}
                disabled={isCancelling}
              >
                戻る
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleCancelConfirmed}
                disabled={isCancelling}
              >
                {isCancelling ? <Loading size="sm" /> : "キャンセルする"}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !isCancelling && setCancelConfirm(null)}
          ></div>
        </div>
      )}
    </div>
  );
};

export default ContractsPage;
